import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import {
  TextField,
  Button,
  Box,
  Card,
  Typography,
  IconButton,
  FormHelperText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {useForm, Controller} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {useOnlineContext} from './OnlineTestContext';
import uuidv4 from 'src/utils/uuidv4';

interface Choice {
  id: string;
  Question: string;
  value: string;
}

interface MatchingQuestionTypeProps {
  setQuestion?: (value: string) => void;
  setCorrectMatches?: (value: Choice[]) => void;
  setAdditionalChoices?: (value: Choice[]) => void;
  setMatchCounter?: (value: string) => void;
  setChoiceCounter?: (value: string) => void;
  question?: string;
  correctMatches?: Choice[];
  additionalChoices?: Choice[];
  matchCounter?: string;
  choiceCounter?: string;
  handleEditQuestion?:any;
}
interface MatchChoice {
  id: string;
  match_the_following_left_value: string;
  match_the_following_right_value: string;
}
interface OptionsExtra {
  id: string;
  match_the_following_additional_right_value: string;
}
interface ChildFormMethods {
  submitForm: () => Promise<any>;
}
const normalize = (s?: string) => (s ?? '').trim().toLowerCase();
const schema = yup
  .object({
    QuestionText: yup.string().required('Question is required'),

    correctMatches: yup
      .array(
        yup.object({
          id: yup.string().required(),
          match_the_following_left_value: yup
            .string()
            .required('Option is required'),
          match_the_following_right_value: yup
            .string()
            .required('Match is required')
        })
      )
      .min(2, 'At least Two correct match is required')
      .required('Correct matches are required')
      // no duplicate RIGHTs within correctMatches
      .test('unique-rights', 'Duplicate match values are not allowed.', arr => {
        if (!arr) return true;
        const rights = arr.map(x =>
          normalize(x.match_the_following_right_value)
        );
        const seen = new Set<string>();
        for (const r of rights) {
          if (!r) continue;
          if (seen.has(r)) return false;
          seen.add(r);
        }
        return true;
      })
      // no RIGHT equals its own LEFT
     ,

    additionalChoices: yup
      .array(
        yup.object({
          id: yup.string().required(),
          match_the_following_additional_right_value: yup
            .string()
            .required('Write the Value')
        })
      )
      // no duplicate among additionalChoices
      .test('unique-choices', 'Duplicate choices are not allowed.', arr => {
        if (!arr) return true;
        const vals = arr.map(x =>
          normalize(x.match_the_following_additional_right_value)
        );
        const seen = new Set<string>();
        for (const v of vals) {
          if (!v) continue;
          if (seen.has(v)) return false;
          seen.add(v);
        }
        return true;
      })
  })
  // cross-array rule: additionalChoices must not overlap any correct match RIGHTs
  .test(
    'no-overlap',
    'Choices must not duplicate any match values.',
    function (all) {
      if (!all) return true;
      const cm = (all.correctMatches ?? [])
        .map(x => normalize(x.match_the_following_right_value))
        .filter(Boolean);
      const ac = (all.additionalChoices ?? [])
        .map(x => normalize(x.match_the_following_additional_right_value))
        .filter(Boolean);
      if (!cm.length || !ac.length) return true;
      const set = new Set(cm);
      return !ac.some(v => set.has(v));
    }
  );

const MatchFollowigOnlineQuestion = forwardRef<
  ChildFormMethods,
  MatchingQuestionTypeProps
>(
  (
    {
      question,
      matchCounter,
      choiceCounter,
      setQuestion,
      setMatchCounter,
      setChoiceCounter,
      handleEditQuestion
    },
    ref
  ) => {
    const {onlineData, setOnlineData} = useOnlineContext();
    const [correctMatchesValue, setCorrectMatches] = useState<MatchChoice[]>(
      handleEditQuestion?.correctMatches || []
    );
    const [additionalChoices, setAdditionalChoices] = useState<OptionsExtra[]>(
      handleEditQuestion?.additionalChoices || []
    );
    const {
      control,
      clearErrors,
      watch,
      setValue,
      handleSubmit,
      trigger,
      setError,
      formState: {errors}
    } = useForm({
      mode: 'onSubmit',
      defaultValues: {
        QuestionText: handleEditQuestion?.QuestionText || '',
        correctMatches: correctMatchesValue || [],
        additionalChoices: additionalChoices || []
      },
      resolver: yupResolver(schema)
    });
    useEffect(() => {
      setValue('correctMatches', correctMatchesValue);
      //   const ValidationError = async () => await trigger('correctMatches');
      //   ValidationError();
    }, [correctMatchesValue]);

    useEffect(() => {
      setValue('additionalChoices', additionalChoices);
      //   const ValidationError = async () => await trigger('correctMatches');
      //   ValidationError();
    }, [additionalChoices]);

    useImperativeHandle(ref, () => ({
      submitForm: () =>
        new Promise((resolve, reject) => {
          // Run your custom checks
          const hasCustomErrors =
            correctMatchesValue.some(m => !!getMatchError(m)) ||
            additionalChoices.some(c => !!getChoiceError(c));

          if (hasCustomErrors) {
            // surface a form-level error for visibility
            setError('root.custom', {
              type: 'manual',
              message: 'Fix duplicate/overlapping values before submitting.'
            });
            // reject(new Error('Validation failed'));
            return;
          }

          handleSubmit(
            data => resolve(data), // valid
            invalid => reject(invalid) // invalid per Yup
          )();
        })
    }));

    const choicesValue = watch('correctMatches');
    const additionalValue = watch('additionalChoices');
    const norm = (s: string) => s.trim().toLowerCase();

    // Build lookup maps once per render
    const rightMap = new Map<string, string>(); // value -> count in matches
    const choiceMap = new Map<string, string>(); // value -> count in choices

    correctMatchesValue.forEach(m => {
      const v = norm(m.match_the_following_right_value || '');
      if (!v) return;
      rightMap.set(v, (rightMap.get(v) || ''));
    });

    additionalChoices.forEach(c => {
      const v = norm(c.match_the_following_additional_right_value || '');
      if (!v) return;
      choiceMap.set(v, (choiceMap.get(v) || ''));
    });
    const generateUniqueId = (): string => {
      const currentIds = [
        ...(Array.isArray(handleEditQuestion?.correctMatches)
          ? handleEditQuestion.correctMatches.map((data: {id: any}) =>
              typeof data.id === 'string' ? data.id : 0
            )
          : []),
        ...(Array.isArray(choicesValue)
          ? choicesValue.map((field: {id: string}) => field.id)
          : [])
      ];
      if (currentIds.length > 0) {
        const maxId = Math.max(...currentIds);
        return uuidv4();
      }

      return uuidv4(); // Default to 1 if the array is empty or undefined
    };

    const handleAddChoice = async () => {
      const newId = generateUniqueId();
      if (setCorrectMatches && correctMatchesValue) {
        const newChoice = {
          id: newId,
          match_the_following_left_value: '',
          match_the_following_right_value: ''
        };
        setCorrectMatches([...correctMatchesValue, newChoice]);
      }
      if (correctMatchesValue.length >= 1) {
        clearErrors('correctMatches');
      }
      // await trigger('correctMatches');
    };

    // Handle deleting a choice
    const handleDeleteChoice = async (id: string) => {
      if (setCorrectMatches && correctMatchesValue) {
        const valuess = correctMatchesValue.filter(
          (choice: {id: string}) => choice.id !== id
        );
        setCorrectMatches(valuess);
      }
      //   const correctChoice = watch('correctMatches');
      //   if (string(correctChoice) === id) {
      //     setValue('correctMatches', 1);
      //   }
      await trigger('correctMatches');
    };
    const generateUniqueIdAdd = (): string => {
      const currentIds = [
        ...(Array.isArray(handleEditQuestion?.additionalChoices)
          ? handleEditQuestion.additionalChoices.map((data: {id: any}) =>
              typeof data.id === 'string' ? data.id : 0
            )
          : []),
        ...(Array.isArray(additionalValue)
          ? additionalValue.map((field: {id: string}) => field.id)
          : [])
      ];
      if (currentIds.length > 0) {
        const maxId = Math.max(...currentIds);
        return uuidv4();
      }

      return uuidv4(); // Default to 1 if the array is empty or undefined
    };

    const handleAddChoiceAdditional = async () => {
      setAdditionalChoices(prevChoices => [
        ...prevChoices,
        {
          id: uuidv4(), // Generate a new temporary ID
          match_the_following_additional_right_value: ''
        }
      ]);
      // await trigger('additionalChoices');
    };

    // Handle deleting a choice
    const handleDeleteChoiceAdd = async (id: string) => {
      if (setAdditionalChoices && additionalChoices) {
        const valuess = additionalChoices.filter(
          (choice: {id: string}) => choice.id !== id
        );
        setAdditionalChoices(valuess);
      }
      //   const correctChoice = watch('correctMatches');
      //   if (string(correctChoice) === id) {
      //     setValue('correctMatches', 1);
      //   }
      await trigger('additionalChoices');
    };
    const handleFormSubmit = (data: any) => {
      console.log('Form Submitted:', data);
    };
    const getMatchError = (match: MatchChoice) => {
      const v = norm(match.match_the_following_right_value || '');
      if (!v) return ''; // don't flag empty as duplicate

      // 1) same as its own left
      // if (v === norm(match.match_the_following_left_value || "")) {
      //   return "Right value cannot be the same as its question.";
      // }
      // 2) duplicate among match.value's
      if (rightMap.get(v) ) {
        return 'This match value duplicates another match.';
      }
      // 3) duplicate with any additional choice
      if (choiceMap.get(v)  ) {
        return 'This match value duplicates a choice.';
      }
      return '';
    };

    const getChoiceError = (choice: OptionsExtra) => {
      const v = norm(choice.match_the_following_additional_right_value || '');
      if (!v) return '';

      // duplicate among choices
      if (choiceMap.get(v) ) {
        return 'This choice duplicates another choice.';
      }
      // duplicate with any match.value
      if (rightMap.get(v) ) {
        return 'This choice duplicates a match value.';
      }
      return '';
    };
    return (
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Card variant="outlined" sx={{mx: 'auto', padding: '1vh'}}>
          <Typography variant="h5" sx={{mb: 2}}>
            Create a Question
          </Typography>

          <Controller
            name="QuestionText"
            control={control}
            render={({field}) => (
              <TextField
                fullWidth
                label="Question"
                variant="outlined"
                {...field}
                error={!!errors.QuestionText}
                helperText={errors.QuestionText?.message}
                sx={{mb: 2}}
              />
            )}
          />

          <Box sx={{mb: 2}}>
            <Typography variant="h6">Correct matches</Typography>
            {correctMatchesValue &&
              correctMatchesValue.map((match, index) => {
                const err = getMatchError(match);
                return (
                  <Box
                    key={match.id}
                    sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                    <Controller
                      name={`correctMatches.${index}.match_the_following_left_value`}
                      control={control}
                      render={({field}) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={`Option ${index + 1}`}
                          variant="outlined"
                          onChange={e => {
                            const updatedChoices = correctMatchesValue.map(
                              item =>
                                item.id === match.id
                                  ? {
                                      ...item,
                                      match_the_following_left_value:
                                        e.target.value
                                    }
                                  : item
                            );
                            if (setCorrectMatches) {
                              setCorrectMatches(updatedChoices);
                            }
                            field.onChange(e); // Ensure the field value is updated
                          }}
                          onBlur={e => {
                            // Optional: Handle blur events for validation
                            field.onBlur();
                          }}
                          error={
                            !!errors.correctMatches?.[index]
                              ?.match_the_following_left_value
                          }
                          helperText={
                            errors.correctMatches?.[index]
                              ?.match_the_following_left_value?.message
                          }
                          sx={{mr: 1}}
                        />
                      )}
                    />
                    <Controller
                      name={`correctMatches.${index}.match_the_following_right_value`}
                      control={control}
                      render={({field}) => (
                        <TextField
                          fullWidth
                          label={`Match ${index + 1}`}
                          variant="outlined"
                          {...field}
                          onChange={e => {
                            const updatedChoices = correctMatchesValue.map(
                              item =>
                                item.id === match.id
                                  ? {
                                      ...item,
                                      match_the_following_right_value:
                                        e.target.value
                                    }
                                  : item
                            );
                            if (setCorrectMatches) {
                              setCorrectMatches(updatedChoices);
                            }
                            field.onChange(e); // Ensure the field value is updated
                          }}
                          onBlur={e => {
                            // Optional: Handle blur events for validation
                            field.onBlur();
                          }}
                          error={
                            Boolean(err) ||
                            !!errors.correctMatches?.[index]
                              ?.match_the_following_right_value
                          }
                          helperText={
                            err ||
                            errors.correctMatches?.[index]
                              ?.match_the_following_right_value?.message
                          }
                          sx={{mr: 1}}
                        />
                      )}
                    />
                    <IconButton
                      onClick={() => {
                        handleDeleteChoice(match.id);
                      }}
                      color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                );
              })}
            {errors && (
              <FormHelperText error sx={{px: 2}}>
                {errors?.correctMatches?.root?.message ||
                  errors?.correctMatches?.message}
              </FormHelperText>
            )}
            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
              <Button
                onClick={handleAddChoice}
                variant="contained"
                color="info">
                Add Match
              </Button>
            </div>
          </Box>

          <Box sx={{mb: 2}}>
            <Typography variant="h6">Additional choices</Typography>
            <p style={{fontSize: 'small', marginBottom: '4px'}}>
              The choices added here will be displayed as additional matches
            </p>
            {additionalChoices &&
              additionalChoices.map((choice, index) => {
                const err = getChoiceError(choice);
                return (
                  <Box
                    key={choice.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mb: '4px'
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                      <Controller
                        name={`additionalChoices.${index}.match_the_following_additional_right_value`}
                        control={control}
                        render={({field}) => (
                          <TextField
                            label={`Choice ${index + 1}`}
                            variant="outlined"
                            {...field}
                            onChange={e => {
                              const updatedChoices = additionalChoices.map(
                                item =>
                                  item.id === choice.id
                                    ? {
                                        ...item,
                                        match_the_following_additional_right_value:
                                          e.target.value
                                      }
                                    : item
                              );
                              if (setAdditionalChoices) {
                                setAdditionalChoices(updatedChoices);
                              }
                              field.onChange(e); // Ensure the field value is updated
                            }}
                            onBlur={e => {
                              // Optional: Handle blur events for validation
                              field.onBlur();
                            }}
                            error={
                              Boolean(err) ||
                              !!errors.additionalChoices?.[index]
                                ?.match_the_following_additional_right_value
                            }
                            helperText={
                              err ||
                              errors.additionalChoices?.[index]
                                ?.match_the_following_additional_right_value
                                ?.message
                            }
                            sx={{mr: 1}}
                          />
                        )}
                      />
                      <IconButton
                        onClick={() => {
                          handleDeleteChoiceAdd(choice.id);
                        }}
                        color="error">
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </Box>
                );
              })}
            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
              <Button
                onClick={() => {
                  handleAddChoiceAdditional();
                }}
                variant="contained"
                color="info">
                Add Additional Choice
              </Button>
            </div>
          </Box>
        </Card>
      </form>
    );
  }
);

export default MatchFollowigOnlineQuestion;
